import { Box } from "@mui/material";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { type SystemChipItem, SystemChips } from "@/ui/Clickable/TabsAndChips/SystemChips/SystemChips";
import { EyebrowLabel } from "@/ui/molecules/EyebrowLabel";

type Props = {
	/** The department's partial shift lengths, in hours. */
	lengths: number[];
	/** The length the editor holds right now, in hours. */
	selectedLength: number;
	onPick: (length: number) => void;
};

/** `EditTimePopover`'s partial shift lengths for a finger: touch chips under the sheet's eyebrow. */
export const EditTimeTouchLengths: FC<Props> = ({ lengths, selectedLength, onPick }) => {
	const { t } = useTranslation();
	const items = useMemo<SystemChipItem<number>[]>(
		() => lengths.map((length) => ({ value: length, label: t("edit.time.touch.hours", { hours: length }) })),
		[lengths, t],
	);
	return (
		<Box component="section" sx={{ mt: 2.5 }}>
			<EyebrowLabel component="h2" sx={{ m: 0, mb: 1 }}>
				{t("choose.shift.duration")}
			</EyebrowLabel>
			<SystemChips<number>
				items={items}
				selectedItems={items.filter((item) => item.value === selectedLength)}
				onChange={(item) => onPick(item.value)}
				variant="filled"
				touchTarget
				// Start-aligned like the labels above them; without it a short row is centred in the sheet.
				forceFitContent
				getItemDataTestId={(item) => `edit-time-duration-${item.value}`}
				rootSx={{ justifyContent: "flex-start" }}
			/>
		</Box>
	);
};
